import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HTTPCONSTANTS } from 'src/app/http/http-constants';

/** Access state, decided by the API from the publication's launch window. */
export type PublicationPhase = 'upcoming' | 'complimentary' | 'on_sale' | 'closed';

export interface PublicationPreviewPage {
  page: number;
  url: string;
  thumb: string;
}

export interface PublicationLanding {
  standfirst?: string;
  byline?: string;
  format?: string;
  shareImage?: string;
  highlights?: { value: string; label: string }[];
  example?: { registration: string; bought: string; boughtNote: string; sold: string; soldNote: string };
  contents?: { page: number; title: string }[];
}

export interface Publication {
  slug: string;
  series: string;
  issueLabel: string;
  title: string;
  subtitle: string;
  edition: string;
  publishedOn: string;
  description: string;
  landing: PublicationLanding;
  coverUrl: string;
  previewPages: PublicationPreviewPage[];
  pageCount: number;
  phase: PublicationPhase;
  launchAt: string | null;
  complimentaryEndsAt: string | null;
  complimentaryDays: number;
  priceMinor: number | null;
  currency: string;
  charityNote: string | null;
  access: { signedIn: boolean; entitled: boolean; source: string | null; grantedAt: string | null; lastPage: number | null };
}

export interface ReaderPage {
  page: number;
  w: number;
  h: number;
  text: string;
}

export interface ReaderManifest {
  slug: string;
  title: string;
  pageCount: number;
  version: string;
  pages: ReaderPage[];
  lastPage: number | null;
}

const CONTEXT_KEY = 'pla-guide-context';
const CONTEXT_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'];

/** /market-guides/{slug}: the guide's public landing page. */
export const guidePath = (slug: string) => `/market-guides/${slug}`;
export const guideReaderPath = (slug: string) => `/market-guides/${slug}/read`;

/**
 * PlateX Market Guides: publication data, complimentary claims and the
 * entitled reader. Every call goes to /api/Publications; page images are
 * fetched with the session token (HttpClient + interceptor) because the API
 * streams them only to entitled users.
 */
@Injectable({ providedIn: 'root' })
export class MarketGuidesService {
  constructor(private http: HttpClient) {}

  private get base(): string {
    return `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/Publications`;
  }

  list(): Observable<Publication[]> {
    return this.http.get<Publication[]>(this.base);
  }

  get(slug: string): Observable<Publication> {
    return this.http.get<Publication>(`${this.base}/${encodeURIComponent(slug)}`);
  }

  claim(slug: string): Observable<Publication> {
    return this.http.post<Publication>(`${this.base}/${encodeURIComponent(slug)}/claim`, {
      context: this.readContext(),
    });
  }

  reader(slug: string): Observable<ReaderManifest> {
    return this.http.get<ReaderManifest>(`${this.base}/${encodeURIComponent(slug)}/reader`);
  }

  pageImage(slug: string, page: number, size: 'm' | 'l'): Observable<string> {
    return this.http
      .get(`${this.base}/${encodeURIComponent(slug)}/pages/${page}?size=${size}`, { responseType: 'blob' })
      .pipe(map((b) => URL.createObjectURL(b)));
  }

  progress(slug: string, page: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${encodeURIComponent(slug)}/progress`, { page });
  }

  /**
   * Remember where a visitor came from (Instagram bio link, advert utm_*),
   * so a claim made after sign-in still records it. First touch wins within
   * the browser session.
   */
  rememberContext(query: { [k: string]: string }, landing: string): void {
    try {
      if (sessionStorage.getItem(CONTEXT_KEY)) return;
      const ctx: { [k: string]: string } = { landing };
      CONTEXT_PARAMS.forEach((k) => {
        if (query[k]) ctx[k] = String(query[k]).slice(0, 120);
      });
      sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(ctx));
    } catch (e) {
      /* storage unavailable: claim without context */
    }
  }

  private readContext(): { [k: string]: string } {
    try {
      return JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }
}

/** "10 November" in UK time. */
export function ukDay(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'Europe/London' });
}

/** Pence to "£14.95". */
export function pounds(minor: number | null): string {
  return minor == null ? '' : '£' + (minor / 100).toFixed(2).replace(/\.00$/, '');
}
